/**
 * WebGL2 plumbing for the lit pipeline: GLSL ES 3.00 program compilation,
 * float/byte render targets, the tiled rotation-noise texture, the hemisphere
 * sample kernel, and a fullscreen-triangle helper for the post passes.
 *
 * Kept separate from litRenderer so the renderer reads as pipeline orchestration,
 * not GL boilerplate.
 */

export type GL = WebGL2RenderingContext;

export function compileProgram(gl: GL, vs: string, fs: string): WebGLProgram {
  const compile = (type: number, src: string): WebGLShader => {
    const s = gl.createShader(type)!;
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      throw new Error('lit shader: ' + gl.getShaderInfoLog(s) + '\n' + src);
    }
    return s;
  };
  const p = gl.createProgram()!;
  gl.attachShader(p, compile(gl.VERTEX_SHADER, vs));
  gl.attachShader(p, compile(gl.FRAGMENT_SHADER, fs));
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error('lit link: ' + gl.getProgramInfoLog(p));
  return p;
}

/** Cache uniform locations for a program as a name→location map. */
export function uniforms(gl: GL, prog: WebGLProgram, names: string[]): Record<string, WebGLUniformLocation | null> {
  const out: Record<string, WebGLUniformLocation | null> = {};
  for (const n of names) out[n] = gl.getUniformLocation(prog, n);
  return out;
}

export interface Target {
  fbo: WebGLFramebuffer;
  textures: WebGLTexture[];
  width: number;
  height: number;
}

interface AttachSpec {
  internal: number; // e.g. gl.RGBA16F, gl.R8
  format: number; // e.g. gl.RGBA, gl.RED
  type: number; // e.g. gl.HALF_FLOAT, gl.UNSIGNED_BYTE
  filter?: number; // gl.NEAREST | gl.LINEAR (default LINEAR)
}

function makeTex(gl: GL, w: number, h: number, s: AttachSpec): WebGLTexture {
  const t = gl.createTexture()!;
  const filter = s.filter ?? gl.LINEAR;
  gl.bindTexture(gl.TEXTURE_2D, t);
  gl.texImage2D(gl.TEXTURE_2D, 0, s.internal, w, h, 0, s.format, s.type, null);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  return t;
}

/**
 * A render target with N colour attachments (MRT) and an optional depth
 * renderbuffer. Recreated whenever the framebuffer size changes.
 */
export function createTarget(gl: GL, w: number, h: number, specs: AttachSpec[], depth: boolean): Target {
  const fbo = gl.createFramebuffer()!;
  gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
  const textures = specs.map((s, i) => {
    const t = makeTex(gl, w, h, s);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0 + i, gl.TEXTURE_2D, t, 0);
    return t;
  });
  if (depth) {
    const rb = gl.createRenderbuffer()!;
    gl.bindRenderbuffer(gl.RENDERBUFFER, rb);
    gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT24, w, h);
    gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, rb);
  }
  gl.drawBuffers(specs.map((_, i) => gl.COLOR_ATTACHMENT0 + i));
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  return { fbo, textures, width: w, height: h };
}

export function deleteTarget(gl: GL, t: Target): void {
  gl.deleteFramebuffer(t.fbo);
  for (const tex of t.textures) gl.deleteTexture(tex);
}

/** Attachment specs, resolved lazily so we can read the float-ext-gated enums. */
export const GBUFFER_SPECS = (gl: GL): AttachSpec[] => [
  // NEAREST: point-sampled so SSAO doesn't read positions/normals interpolated across depth edges.
  { internal: gl.RGBA16F, format: gl.RGBA, type: gl.HALF_FLOAT, filter: gl.NEAREST }, // x,y,z view normal + linear view depth in .w
  { internal: gl.RGBA16F, format: gl.RGBA, type: gl.HALF_FLOAT, filter: gl.NEAREST }, // view-space position (xyz)
];
export const AO_SPEC = (gl: GL): AttachSpec[] => [{ internal: gl.R8, format: gl.RED, type: gl.UNSIGNED_BYTE }];

/**
 * 4×4 tiled noise: random rotations about the view Z axis (xy unit vectors).
 * Sampled with NEAREST + REPEAT to break up the kernel per-pixel.
 */
export function createNoiseTexture(gl: GL): WebGLTexture {
  const size = 4;
  const data = new Uint8Array(size * size * 4);
  for (let i = 0; i < size * size; i++) {
    const a = Math.random() * Math.PI * 2;
    data[i * 4 + 0] = Math.round((Math.cos(a) * 0.5 + 0.5) * 255);
    data[i * 4 + 1] = Math.round((Math.sin(a) * 0.5 + 0.5) * 255);
    data[i * 4 + 2] = 0;
    data[i * 4 + 3] = 255;
  }
  const t = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, t);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, size, size, 0, gl.RGBA, gl.UNSIGNED_BYTE, data);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
  return t;
}

/**
 * A cosine-ish hemisphere kernel oriented around +Z, packed flat as xyz triples.
 * Samples are pushed toward the origin (more taps near the centre) so the near
 * radius resolves tight creases well. Returned as a Float32Array of length n*3.
 */
export function hemisphereKernel(n: number): Float32Array {
  const k = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    let x = Math.random() * 2 - 1;
    let y = Math.random() * 2 - 1;
    let z = Math.random(); // +Z hemisphere
    const l = Math.hypot(x, y, z) || 1;
    x /= l;
    y /= l;
    z /= l;
    // Bias the magnitude toward the centre: scale ~ lerp(0.1, 1, t^2).
    const t = (i + 1) / n;
    const s = 0.1 + 0.9 * t * t;
    k[i * 3 + 0] = x * s;
    k[i * 3 + 1] = y * s;
    k[i * 3 + 2] = z * s;
  }
  return k;
}

/** A single oversized triangle that covers the screen; no VBO data needed. */
export function makeFullscreenVAO(gl: GL): WebGLVertexArrayObject {
  const vao = gl.createVertexArray()!;
  // The vertex shader synthesises positions from gl_VertexID, so an empty VAO
  // bound during a 3-vertex draw is all we need.
  return vao;
}
